import React, { useEffect, useState, useMemo } from "react";
import { ChevronsUpDown, Check, Building2, Layers, Search } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";

/**
 * Admin account switcher — pick "All accounts" (aggregate) or jump into a single
 * client's data. The selection is persisted globally (AuthContext.viewAs) so it
 * sticks across every tab and a page refresh.
 */
export default function AccountSwitcher() {
  const { viewAs, setViewAs } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/accounts")
      .then(({ data }) => { if (!cancelled) setAccounts(data.accounts || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const activeAccount = useMemo(
    () => accounts.find((a) => a.id === viewAs),
    [accounts, viewAs]
  );
  const isAll = viewAs === "all" || !viewAs;

  const contextLabel = activeAccount ? activeAccount.company_name : "All accounts";
  const contextSub = activeAccount
    ? `Viewing as · ${activeAccount.boxhero_account_id || "client"}`
    : "Aggregated across all stores";

  const pickAll = () => { setViewAs("all", "All accounts"); setOpen(false); };
  const pickAccount = (a) => { setViewAs(a.id, a.company_name); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="account-switcher-trigger"
          className="flex items-center gap-3 pl-3 sm:pl-5 sm:border-l border-bh-divider hover:opacity-90 transition-opacity"
        >
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold text-bh-navy-deep leading-tight max-w-[180px] truncate">{contextLabel}</p>
            <p className="text-xs text-bh-muted">{contextSub}</p>
          </div>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${activeAccount ? "bg-bh-orange text-bh-navy-deep" : "bg-bh-navy text-white"}`}>
            {activeAccount ? <Building2 className="w-5 h-5" /> : <Layers className="w-5 h-5" />}
          </div>
          <ChevronsUpDown className="w-4 h-4 text-bh-muted shrink-0" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0" data-testid="account-switcher-content">
        <Command>
          <div className="flex items-center border-b border-bh-divider px-3" cmdk-input-wrapper="">
            <Search className="mr-2 h-4 w-4 shrink-0 text-bh-muted" />
            <CommandInput placeholder="Search client accounts…" className="border-0" data-testid="account-switcher-search" />
          </div>
          <CommandList className="max-h-[320px]">
            <CommandEmpty>No accounts found.</CommandEmpty>

            <CommandGroup heading="Overview">
              <CommandItem
                value="All accounts aggregate every store"
                onSelect={pickAll}
                data-testid="switch-to-all"
                className="gap-2 cursor-pointer"
              >
                <Layers className="w-4 h-4 text-bh-blue" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-bh-navy-deep">All accounts</p>
                  <p className="text-xs text-bh-muted">Aggregated view across every store</p>
                </div>
                {isAll && <Check className="w-4 h-4 text-bh-orange" />}
              </CommandItem>
            </CommandGroup>

            <CommandGroup heading={`Client accounts (${accounts.length})`}>
              {accounts.map((a) => (
                <CommandItem
                  key={a.id}
                  value={`${a.company_name} ${a.boxhero_account_id || ""} ${a.primary_email || ""}`}
                  onSelect={() => pickAccount(a)}
                  data-testid={`switch-to-${a.id}`}
                  className="gap-2 cursor-pointer"
                >
                  <div className="w-6 h-6 rounded bg-bh-bg text-bh-navy-deep flex items-center justify-center text-[10px] font-bold shrink-0">
                    {(a.company_name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-bh-navy-deep truncate">{a.company_name}</p>
                    <p className="text-xs text-bh-muted truncate">{a.boxhero_account_id || a.primary_email || "—"}</p>
                  </div>
                  {a.id === viewAs && <Check className="w-4 h-4 text-bh-orange shrink-0" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
